import { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './store/auth';

// Pages
import LoginPage from './pages/LoginPage';
import VerifyPage from './pages/VerifyPage';
import WelcomePage from './pages/WelcomePage';
import ProfileSetupPage from './pages/ProfileSetupPage';
import DashboardPage from './pages/DashboardPage';
import PhaseDetailPage from './pages/PhaseDetailPage';
import ContentReviewPage from './pages/ContentReviewPage';

// Admin Pages
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import AdminClientDetailPage from './pages/admin/AdminClientDetailPage';
import AdminCoachInboxPage from './pages/admin/AdminCoachInboxPage';

// Layout
import AuthLayout from './components/layout/AuthLayout';
import AppLayout from './components/layout/AppLayout';
import AdminLayout from './components/layout/AdminLayout';

// Helper to check if profile is complete
function isProfileComplete(user: any) {
  return !!(user?.business_type && user?.city && user?.country);
}

function App() {
  const { isLoading, isAuthenticated, user, loadUser } = useAuthStore();

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Profile completeness check
  const profileComplete = isAuthenticated && isProfileComplete(user);
  const hasSeenWelcome = localStorage.getItem('has_seen_welcome') === 'true';

  return (
    <Routes>
      {/* Public routes */}
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/verify" element={<VerifyPage />} />
      </Route>

      {/* Onboarding routes (protected but no layout) */}
      {isAuthenticated && !profileComplete && (
        <>
          <Route path="/onboarding/welcome" element={<WelcomePage />} />
          <Route path="/onboarding/setup" element={<ProfileSetupPage />} />
          <Route path="*" element={<Navigate to="/onboarding/setup" replace />} />
        </>
      )}

      {/* Show welcome for first-time users with complete profiles */}
      {isAuthenticated && profileComplete && !hasSeenWelcome && (
        <Route path="*" element={<WelcomePage />} />
      )}

      {/* Admin routes */}
      {isAuthenticated && profileComplete && hasSeenWelcome && user?.is_admin && (
        <Route element={<AdminLayout />}>
          <Route path="/admin" element={<AdminDashboardPage />} />
          <Route path="/admin/clients/:id" element={<AdminClientDetailPage />} />
          <Route path="/admin/coach" element={<AdminCoachInboxPage />} />
        </Route>
      )}

      {/* Protected app routes (for users with complete profiles) */}
      {isAuthenticated && profileComplete && hasSeenWelcome && (
        <Route element={<AppLayout />}>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/phases/:slug" element={<PhaseDetailPage />} />
          <Route path="/phases/:slug/review" element={<ContentReviewPage />} />
        </Route>
      )}

      {/* Redirect non-authenticated users to login */}
      {!isAuthenticated && <Route path="*" element={<Navigate to="/login" replace />} />}

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
